import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Typography,
  Stack,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  Event,
  Close,
  CalendarMonth,
  AccessTime,
  LocationOn,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import useFetchDataWithPagination from "../../../utility/useFetchDataWithPagination";
import usePostData from "../../../utility/usePostData";

const EventCalendar = () => {
  const { isLoading, error, submitData } = usePostData();
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    time: "",
    location: "",
    category: "",
  });

  const { data, isLoading: loading, paginate, refreshSearch } =
    useFetchDataWithPagination("events");
  const [eventsList, setEventsList] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    if (data?.data?.data) {
      setEventsList(data.data.data.data);
    }
  }, [data]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      date: "",
      time: "",
      location: "",
      category: "",
    });
    setErrorMessage("");
    setEditMode(false);
    setCurrentEvent(null);
  };

  const handleOpenDialog = (event = null) => {
    if (event) {
      setEditMode(true);
      setCurrentEvent(event);
      setFormData({
        title: event.title || "",
        description: event.description || "",
        date: event.date || "",
        time: event.time || "",
        location: event.location || "",
        category: event.category || "",
      });
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.title || !formData.date || !formData.time) {
      setErrorMessage("Veuillez remplir tous les champs obligatoires");
      return;
    }

    const endpoint = editMode ? `events/${currentEvent.id}` : "events";
    const method = editMode ? "PUT" : "POST";

    submitData(endpoint, formData, method);
    refreshSearch();
    handleCloseDialog();
  };

  const handleDelete = (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet événement ?")) {
      submitData(`events/${id}`, null, "DELETE");
      refreshSearch();
    }
  };

  const getEventsByDate = () => {
    const grouped = {};
    if (Array.isArray(eventsList)) {
      eventsList.forEach((event) => {
        const date = event.date;
        if (!grouped[date]) grouped[date] = [];
        grouped[date].push(event);
      });
    }
    return grouped;
  };

  const eventsGrouped = getEventsByDate();
  const sortedDates = Object.keys(eventsGrouped).sort();

  const categories = Array.isArray(eventsList)
    ? [...new Set(eventsList.map((e) => e.category).filter(Boolean))]
    : [];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {isLoading && <LinearProgress color="primary" sx={{ mb: 2 }} />}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 4 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <CalendarMonth color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h4" fontWeight={700} color="primary">
            Calendrier des Événements
          </Typography>
        </Stack>

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: "primary.main" }}
        >
          Nouvel Événement
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.message}
        </Alert>
      )}

      {loading && <LinearProgress color="secondary" sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {sortedDates.map((date) => (
          <Grid item xs={12} key={date}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                {new Date(date).toLocaleDateString("fr-FR", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Typography>

              <Grid container spacing={2}>
                {eventsGrouped[date].map((event, idx) => (
                  <Grid item xs={12} md={6} lg={4} key={idx}>
                    <Card
                      sx={{
                        height: "100%",
                        transition: "transform 0.2s, box-shadow 0.2s",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 4,
                        },
                      }}
                    >
                      <CardContent>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          sx={{ mb: 1 }}
                        >
                          <Typography variant="h6" fontWeight={600}>
                            {event.title}
                          </Typography>
                          <Stack direction="row" spacing={0.5}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenDialog(event)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(event.id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>

                        {event.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            {event.description}
                          </Typography>
                        )}

                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <AccessTime fontSize="small" color="action" />
                            <Typography variant="body2">{event.time}</Typography>
                          </Stack>

                          {event.location && (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <LocationOn fontSize="small" color="action" />
                              <Typography variant="body2">
                                {event.location}
                              </Typography>
                            </Stack>
                          )}

                          {event.category && (
                            <Chip
                              label={event.category}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ mt: 1, alignSelf: "flex-start" }}
                            />
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {eventsList.length === 0 && !loading && (
        <Paper
          elevation={0}
          sx={{
            textAlign: "center",
            py: 8,
            bgcolor: "grey.50",
            borderRadius: 2,
          }}
        >
          <Event sx={{ fontSize: 80, color: "grey.300", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Aucun événement planifié
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{ mt: 2 }}
          >
            Créer votre premier événement
          </Button>
        </Paper>
      )}

      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        {paginate()}
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editMode ? "Modifier l'événement" : "Nouvel événement"}
            </Typography>
            <IconButton onClick={handleCloseDialog}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                required
                fullWidth
                label="Titre de l'événement"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
              />

              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
              />

              <TextField
                required
                fullWidth
                label="Date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                required
                fullWidth
                label="Heure"
                name="time"
                type="time"
                value={formData.time}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="Lieu"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
              />

              <TextField
                fullWidth
                label="Catégorie"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                placeholder="Ex: Conférence, Atelier, Réunion"
              />
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {editMode ? "Mettre à jour" : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EventCalendar;